import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface CaseStudySpotlightProps {
  spotlight: {
    id: number;
    title: string;
    clientName: string;
    clientType: string;
    industry: string;
    challenge: string;
    solution: string;
    results: string[];
    beforeMetrics: {
      portfolioValue: string;
      annualReturn: string;
      riskLevel: string;
    };
    afterMetrics: {
      portfolioValue: string;
      annualReturn: string;
      riskLevel: string;
    };
    timeframe: string;
    featuredImage: string;
    featuredImageAlt: string;
  };
}

const CaseStudySpotlight = ({ spotlight }: CaseStudySpotlightProps) => {
  return (
    <div className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-lg p-6 lg:p-8 mb-8">
      <div className="flex items-center space-x-2 mb-4">
        <Icon name="StarIcon" size={24} variant="solid" className="text-cta" />
        <h2 className="font-heading font-bold text-2xl text-foreground">
          Featured Case Study
        </h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="relative rounded-lg overflow-hidden mb-4">
            <AppImage
              src={spotlight.featuredImage}
              alt={spotlight.featuredImageAlt}
              className="w-full h-80 object-cover"
            />
            <div className="absolute top-4 left-4">
              <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-cta text-cta-foreground">
                {spotlight.clientType}
              </span>
            </div>
          </div>

          <h3 className="font-heading font-bold text-xl text-foreground mb-2">
            {spotlight.title}
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            {spotlight.clientName} • {spotlight.industry} • {spotlight.timeframe}
          </p>

          <div className="space-y-4">
            <div>
              <h4 className="font-heading font-semibold text-sm text-foreground mb-2 flex items-center space-x-2">
                <Icon name="ExclamationTriangleIcon" size={16} variant="outline" className="text-warning" />
                <span>Challenge</span>
              </h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {spotlight.challenge}
              </p>
            </div>

            <div>
              <h4 className="font-heading font-semibold text-sm text-foreground mb-2 flex items-center space-x-2">
                <Icon name="LightBulbIcon" size={16} variant="outline" className="text-accent" />
                <span>Solution</span>
              </h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {spotlight.solution}
              </p>
            </div>
          </div>
        </div>

        <div>
          <div className="bg-card rounded-lg p-6 mb-4">
            <h4 className="font-heading font-semibold text-base text-foreground mb-4 flex items-center space-x-2">
              <Icon name="ChartBarIcon" size={18} variant="outline" className="text-success" />
              <span>Performance Transformation</span>
            </h4>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground mb-2">Before</p>
                <div className="space-y-2">
                  <div>
                    <p className="text-lg font-heading font-bold text-foreground">
                      {spotlight.beforeMetrics.portfolioValue}
                    </p>
                    <p className="text-xs text-muted-foreground">Portfolio Value</p>
                  </div>
                  <div>
                    <p className="text-lg font-heading font-bold text-foreground">
                      {spotlight.beforeMetrics.annualReturn}
                    </p>
                    <p className="text-xs text-muted-foreground">Annual Return</p>
                  </div>
                  <div>
                    <p className="text-lg font-heading font-bold text-foreground">
                      {spotlight.beforeMetrics.riskLevel}
                    </p>
                    <p className="text-xs text-muted-foreground">Risk Level</p>
                  </div>
                </div>
              </div>

              <div className="text-center p-4 bg-success/10 rounded-lg border-2 border-success">
                <p className="text-xs text-success font-semibold mb-2">After</p>
                <div className="space-y-2">
                  <div>
                    <p className="text-lg font-heading font-bold text-success">
                      {spotlight.afterMetrics.portfolioValue}
                    </p>
                    <p className="text-xs text-muted-foreground">Portfolio Value</p>
                  </div>
                  <div>
                    <p className="text-lg font-heading font-bold text-success">
                      {spotlight.afterMetrics.annualReturn}
                    </p>
                    <p className="text-xs text-muted-foreground">Annual Return</p>
                  </div>
                  <div>
                    <p className="text-lg font-heading font-bold text-success">
                      {spotlight.afterMetrics.riskLevel}
                    </p>
                    <p className="text-xs text-muted-foreground">Risk Level</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-lg p-6">
            <h4 className="font-heading font-semibold text-base text-foreground mb-3 flex items-center space-x-2">
              <Icon name="CheckCircleIcon" size={18} variant="solid" className="text-success" />
              <span>Key Results</span>
            </h4>
            <ul className="space-y-2">
              {spotlight.results.map((result, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <Icon
                    name="CheckIcon"
                    size={16}
                    variant="outline"
                    className="text-success mt-0.5 flex-shrink-0"
                  />
                  <span className="text-sm text-muted-foreground">{result}</span>
                </li>
              ))}
            </ul>

            <button className="w-full mt-6 px-6 py-3 bg-primary text-primary-foreground rounded-md font-heading font-semibold text-sm hover:bg-opacity-90 transition-all duration-300 flex items-center justify-center space-x-2">
              <span>Read Complete Case Study</span>
              <Icon name="ArrowRightIcon" size={16} variant="outline" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseStudySpotlight;